/**
 * Markdown Utilities for Finder University
 *
 * Handles semantic block parsing, inline list splitting,
 * and formatting normalization.
 */

/**
 * Preprocess and parse multi-line markdown text into structured semantic blocks.
 */
export function parseMarkdownBlocks(rawText) {
  if (!rawText) return []

  let text = String(rawText).replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // Convert inline numbered lists or question headings without newlines into distinct lines
  // e.g. "**Why create a branch?** 1. **Safety First:**... 2. **Parallel Development:**..."
  text = text.replace(/(^|\n)(\*\*[^*]+?\?\*\*)\s*([A-Za-z0-9•\u2022])/g, '$1$2\n\n$3')
  text = text.replace(/(^|\n)(\*\*[^*]+?:\*\*)\s*([•\u2022]|\d+[\.\)])/g, '$1$2\n\n$3')
  text = text.replace(/([^\n])\s+(\d+[\.\)]\s+)/g, '$1\n$2')
  text = text.replace(/([^\n])\s+([•\u2022]\s+)/g, '$1\n$2')

  const lines = text.split('\n')
  const blocks = []
  let currentList = null
  let currentCode = null
  let currentQuote = null
  let currentParagraph = []

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      blocks.push({ type: 'paragraph', text: currentParagraph.join(' ') })
      currentParagraph = []
    }
  }
  const flushList = () => {
    if (currentList) {
      blocks.push(currentList)
      currentList = null
    }
  }
  const flushQuote = () => {
    if (currentQuote) {
      blocks.push({ type: 'quote', text: currentQuote.lines.join('\n') })
      currentQuote = null
    }
  }

  for (let line of lines) {
    const trimmed = line.trim()

    // Code block fence
    if (trimmed.startsWith('```')) {
      flushParagraph()
      flushList()
      flushQuote()
      if (currentCode) {
        blocks.push({ type: 'code', code: currentCode.lines.join('\n'), lang: currentCode.lang })
        currentCode = null
      } else {
        currentCode = { lang: trimmed.slice(3).trim(), lines: [] }
      }
      continue
    }

    if (currentCode) {
      currentCode.lines.push(line)
      continue
    }

    if (!trimmed) {
      flushParagraph()
      flushList()
      flushQuote()
      continue
    }

    // Heading (# Header)
    const hMatch = trimmed.match(/^(#{1,6})\s+(.+)$/)
    if (hMatch) {
      flushParagraph()
      flushList()
      flushQuote()
      blocks.push({ type: 'heading', level: hMatch[1].length, text: hMatch[2] })
      continue
    }

    // Standalone bold question heading, e.g. "**Why create a branch?**"
    const boldHeadingMatch = trimmed.match(/^\*\*([^*]+(?:\?|:))\*\*$/)
    if (boldHeadingMatch) {
      flushParagraph()
      flushList()
      flushQuote()
      blocks.push({ type: 'heading', level: 3, text: boldHeadingMatch[1] })
      continue
    }

    // Blockquote
    if (trimmed.startsWith('>')) {
      flushParagraph()
      flushList()
      if (!currentQuote) currentQuote = { lines: [] }
      currentQuote.lines.push(trimmed.replace(/^>\s?/, ''))
      continue
    } else {
      flushQuote()
    }

    // Bullet list (- , * , • )
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ') || trimmed.startsWith('\u2022 ')) {
      flushParagraph()
      if (currentList && currentList.type !== 'ul') flushList()
      if (!currentList) currentList = { type: 'ul', items: [] }
      currentList.items.push(trimmed.replace(/^[-*•\u2022]\s+/, ''))
      continue
    }

    // Numbered list (1. or 1) )
    const numMatch = trimmed.match(/^(\d+)[\.\)]\s+(.+)$/)
    if (numMatch) {
      flushParagraph()
      if (currentList && currentList.type !== 'ol') flushList()
      if (!currentList) currentList = { type: 'ol', items: [] }
      currentList.items.push(numMatch[2])
      continue
    }

    // Regular line
    flushList()
    currentParagraph.push(trimmed)
  }

  flushParagraph()
  flushList()
  flushQuote()
  if (currentCode) {
    blocks.push({ type: 'code', code: currentCode.lines.join('\n'), lang: currentCode.lang })
  }

  return blocks
}
